import type {UseQueryResult} from '@tanstack/react-query'
import {hashKey, useQueryClient} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'

import {DefaultSearchConfig} from '../../../components/filter-bar/search-context'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useVerticalGroupedBy} from '../../../features/grouping/hooks/use-vertical-grouped-by'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import {useViews} from '../../../hooks/use-views'
import {
  buildMemexItemsPageQueryKey,
  getGroupIdFromQueryKey,
  type GroupId,
  isQueryKeyForGroups,
  type PaginatedMemexItemsQueryKey,
} from './query-keys'
import {
  type MemexGroupsPageQueryData,
  type MemexItemsPageQueryData,
  type MemexPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  pageParamForNextPlaceholder,
  type PaginatedMemexItemsQueryVariables,
} from './types'
import {useMemexItemsActiveQueriesMetadata} from './use-memex-items-active-queries-metadata'
import {useNextPlaceholderQuery} from './use-next-placeholder-query'
import {usePaginatedMemexItemsActiveQueries} from './use-paginated-memex-items-active-queries'

export function usePaginatedMemexItemsQuery() {
  const variables = usePaginatedMemexItemsQueryVariables()
  const {queries, queryKeys} = usePaginatedMemexItemsActiveQueries(variables)

  // Our queries from usePaginatedMemexItemsActiveQueries can be 1 of 2 types:
  // 1. A query for a page of memex items (queriesForItems: Array<MemexItemsPageQueryData>)
  // 2. A query for a page of groups (queriesForGroups: Array<MemexGroupsPageQueryData>)
  // We need to separate these into 2 arrays, so that consumers of this hook can easily
  // stitch together items data based on those pages, but also provide access to the
  // groups pages for looking up information about the groups and their `pageInfo`.
  const {queriesForItems, queryKeysForItems, queriesForGroups, queryKeysForGroups} = useMemo(() => {
    return separateQueriesForItemsAndGroups(queries, queryKeys)
  }, [queries, queryKeys])

  const data = queriesForItems.map(
    pageQuery => pageQuery.data || {nodes: [], pageInfo: {hasNextPage: false, hasPreviousPage: false}},
  )

  const itemQueriesByGroup = useMemo(() => {
    return groupItemQueriesByGroupId(queriesForItems, queryKeysForItems, queriesForGroups)
  }, [queriesForGroups, queriesForItems, queryKeysForItems])

  const result = {
    data,
    queriesForItems,
    queryKeysForItems,
    queriesForGroups,
    queryKeysForGroups,
    itemQueriesByGroup,
    ...useMemexItemsPagination(queriesForItems, queriesForGroups, queryKeysForItems, queryKeysForGroups, variables),
    ...useMemexItemsGroupPagination(queriesForItems, queryKeysForItems, variables),
  }
  return result
}

function usePaginatedMemexItemsQueryVariables() {
  const {currentView} = useViews()
  const q = DefaultSearchConfig.getSearchQueryFromView(currentView)
  const {sorts} = useSortedBy()
  const sortedBy = useMemo(
    () => sorts.map(sort => ({columnId: sort.column.databaseId, direction: sort.direction})),
    [sorts],
  )
  const {groupedByColumnId: horizontalGroupedByColumnId} = useHorizontalGroupedBy()
  let {groupedByColumnId: verticalGroupedByColumnId} = useVerticalGroupedBy()

  // only board views use vertical grouping, but it's always defaulted for any view type
  if (currentView?.localViewStateDeserialized.viewType !== 'board') {
    verticalGroupedByColumnId = undefined
  }

  return useMemo(
    () => ({
      q,
      sortedBy,
      horizontalGroupedByColumnId,
      verticalGroupedByColumnId,
    }),
    [q, sortedBy, horizontalGroupedByColumnId, verticalGroupedByColumnId],
  )
}

function useMemexItemsPagination(
  itemQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  groupQueries: Array<UseQueryResult<MemexGroupsPageQueryData>>,
  queryKeysForItems: Array<PaginatedMemexItemsQueryKey>,
  queryKeysForGroups: Array<PaginatedMemexItemsQueryKey>,
  variables: PaginatedMemexItemsQueryVariables,
) {
  const {getActiveQueriesMetadata, setActiveQueriesMetadata} = useMemexItemsActiveQueriesMetadata(variables)
  const queryClient = useQueryClient()
  const {setUpNextPlaceholderQueries} = useNextPlaceholderQuery(variables)
  const currentActiveQueryData = getActiveQueriesMetadata()
  // Decide if working with pages of groups or pages of ungrouped items
  const queriesArray = 'groups' in currentActiveQueryData ? groupQueries : itemQueries
  // Get last page query
  const indexOfLastPage = currentActiveQueryData.pageParams.length - 1
  const lastPage = queriesArray[indexOfLastPage]

  const fetchNextPage = useCallback(() => {
    const endCursor = lastPage?.data?.pageInfo.endCursor
    const newPageParam: PageParam = endCursor == null ? pageParamForInitialPage : {after: endCursor}
    setActiveQueriesMetadata({
      ...currentActiveQueryData,
      pageParams: [...currentActiveQueryData.pageParams, newPageParam],
    })
  }, [currentActiveQueryData, lastPage, setActiveQueriesMetadata])

  const invalidateAllQueries = useCallback(async () => {
    const currentQueryKeys = [...queryKeysForItems.concat(queryKeysForGroups)]
    const alreadyRefreshing = currentQueryKeys.some(queryKey => queryKey[3] === pageParamForNextPlaceholder)
    if (alreadyRefreshing) {
      // Ignore if we're already refreshing
      return
    }
    // Set up `next_placeholder` queries with current data
    setUpNextPlaceholderQueries()
    // Remove data for existing query keys, so that we force a re-request of the data
    for (const queryKey of currentQueryKeys) {
      queryClient.removeQueries({queryKey})
    }
  }, [setUpNextPlaceholderQueries, queryKeysForItems, queryKeysForGroups, queryClient])

  const hasNextPage = lastPage?.data == null || lastPage?.data.pageInfo.hasNextPage
  const isFetchingNextPage = lastPage?.isFetching ?? false
  const isRefetching = queriesArray.some(query => query.isRefetching)

  return {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    invalidateAllQueries,
  }
}

function useMemexItemsGroupPagination(
  itemQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  queryKeysForItems: Array<PaginatedMemexItemsQueryKey>,
  variables: PaginatedMemexItemsQueryVariables,
) {
  const {getActiveQueriesMetadata, setActiveQueriesMetadata} = useMemexItemsActiveQueriesMetadata(variables)
  const currentActiveQueryData = getActiveQueriesMetadata()
  const pageParamsByGroupId = useMemo(
    () => ('groups' in currentActiveQueryData ? currentActiveQueryData.groups : {}),
    [currentActiveQueryData],
  )

  const getLastPageForGroup = useCallback(
    (groupId: string) => {
      const pageParamsForGroup = pageParamsByGroupId[groupId] || []
      const lastPageParam = pageParamsForGroup[pageParamsForGroup.length - 1]
      const queryKeyForPageInGroup = buildMemexItemsPageQueryKey(variables, groupId, lastPageParam)
      return getItemPageQueryForQueryKey(queryKeyForPageInGroup, itemQueries, queryKeysForItems)
    },
    [pageParamsByGroupId, itemQueries, queryKeysForItems, variables],
  )

  const fetchNextPageForGroup = useCallback(
    (groupId: string) => {
      const pageQuery = getLastPageForGroup(groupId)
      const endCursor = pageQuery?.data?.pageInfo.endCursor
      const newPageParam: PageParam = endCursor == null ? pageParamForInitialPage : {after: endCursor}

      // Find the current entry for the group and add the new pageParam
      const newGroups = {...pageParamsByGroupId}
      const previousPageParams = pageParamsByGroupId[groupId] || []
      newGroups[groupId] = [...previousPageParams, newPageParam]
      setActiveQueriesMetadata({...currentActiveQueryData, groups: newGroups})
    },
    [currentActiveQueryData, getLastPageForGroup, pageParamsByGroupId, setActiveQueriesMetadata],
  )

  const hasNextPageForGroup = useCallback(
    (groupId: string) => {
      const pageQuery = getLastPageForGroup(groupId)
      // If we're unable to find a query matching the key, then we will return false.
      if (!pageQuery) {
        return false
      }

      // The last page in the group will have a null data value if it hasn't been fetched yet,
      // in which case we still have a `hasNextPage` value of true - otherwise,
      // return the `hasNextPage` value from our query data.
      const lastGroupPageData = pageQuery.data
      return lastGroupPageData == null || lastGroupPageData.pageInfo.hasNextPage
    },
    [getLastPageForGroup],
  )

  const isFetchingNextPageForGroup = useCallback(
    (groupId: string) => {
      const pageQuery = getLastPageForGroup(groupId)
      return pageQuery?.isFetching
    },
    [getLastPageForGroup],
  )

  return {
    fetchNextPageForGroup,
    hasNextPageForGroup,
    isFetchingNextPageForGroup,
  }
}

function getItemPageQueryForQueryKey(
  queryKey: PaginatedMemexItemsQueryKey,
  itemQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  queryKeysForItems: Array<PaginatedMemexItemsQueryKey>,
) {
  // We want to correlate that query key to a query, but since query keys are arrays,
  // we need to hash the key first before performing the comparison
  const hashForQueryKey = hashKey(queryKey)
  const queryKeyIndex = queryKeysForItems.findIndex(key => hashKey(key) === hashForQueryKey)
  return itemQueries[queryKeyIndex]
}

function separateQueriesForItemsAndGroups(
  pageQueries: Array<UseQueryResult<MemexPageQueryData>>,
  pageQueryKeys: Array<PaginatedMemexItemsQueryKey>,
) {
  const queriesForItems: Array<UseQueryResult<MemexItemsPageQueryData>> = []
  const queryKeysForItems: Array<PaginatedMemexItemsQueryKey> = []
  const queriesForGroups: Array<UseQueryResult<MemexGroupsPageQueryData>> = []
  const queryKeysForGroups: Array<PaginatedMemexItemsQueryKey> = []

  for (const [index, query] of pageQueries.entries()) {
    const queryKey = pageQueryKeys[index] as PaginatedMemexItemsQueryKey
    // We want to separate out queries for groups from queries for items based on the query key.
    // We unfortunately cannot rely on the shape of the data, as it might still be undefined,
    // while the data is loading.
    if (isQueryKeyForGroups(queryKey)) {
      queriesForGroups.push(query as UseQueryResult<MemexGroupsPageQueryData>)
      queryKeysForGroups.push(queryKey)
    } else {
      queriesForItems.push(query as UseQueryResult<MemexItemsPageQueryData>)
      queryKeysForItems.push(queryKey)
    }
  }
  return {
    queriesForItems,
    queryKeysForItems,
    queriesForGroups,
    queryKeysForGroups,
  }
}

export type GroupQueries = {
  groupId: NonNullable<GroupId>
  groupValue: string
  queries: Array<UseQueryResult<MemexItemsPageQueryData>>
}

type ItemQueriesByGroup = Record<NonNullable<GroupId>, GroupQueries>

/**
 * Given all our queries for items and groups, we want to map all of the item queries to their corresponding group.
 * If we don't have any group queries, we will return an empty object.
 * Otherwise, we will group all of the item queries by their group id (which is extracted from its query key).
 * Then we will return an object that maps each group id to an array of item queries, along with the group name.
 */
function groupItemQueriesByGroupId(
  itemQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  itemQueryKeys: Array<PaginatedMemexItemsQueryKey>,
  groupQueries: Array<UseQueryResult<MemexGroupsPageQueryData>>,
): ItemQueriesByGroup {
  const itemQueriesByGroup: Record<string, Array<UseQueryResult<MemexItemsPageQueryData>>> = {}

  // If we aren't grouped, we can return early
  if (groupQueries.length === 0) {
    return {}
  }

  // Let's iterate through all of our item query keys and group their corresponding queries by group id.
  for (const [index, queryKey] of itemQueryKeys.entries()) {
    const groupId = getGroupIdFromQueryKey(queryKey)
    const relatedQuery = itemQueries[index]
    if (relatedQuery && groupId) {
      const queriesForGroup = itemQueriesByGroup[groupId] || []
      queriesForGroup.push(relatedQuery)
      itemQueriesByGroup[groupId] = queriesForGroup
    }
  }

  // Each group query in groupQueries represents a page of groups, so let's flatten all of the groups into a single array.
  const groups = groupQueries.flatMap(q => q.data?.groups || [])

  // We want to iterate over each of these groups and extract the group id and group name from each group,
  // and then look up the item queries for that group that we built in the previous step.
  return groups.reduce((groupsById, group) => {
    return {
      ...groupsById,
      [group.groupId]: {
        groupId: group.groupId,
        groupValue: group.groupValue,
        queries: itemQueriesByGroup[group.groupId] || [],
      },
    }
  }, {} as ItemQueriesByGroup)
}
